import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import { Download, Sparkles } from "lucide-react"
import { useEffect, useState } from "react"

import type { SalesOrgLeadDetail } from "@xop/dashboard-shared"

// Dashboard-side shared modules — relative imports (NOT `@/`, which the CRM
// tsconfig remaps to src/crm). Same pattern as ShareMenu on the prospect
// detail page; see docs/crm.md.
import { api, ApiError } from "../../lib/api.js"
import { saveBlob } from "../../lib/download.js"
import { buildLeadPdf, leadPdfFilename } from "../../pages/sales/leads/leadPdf.js"
import { PdfReader } from "@/components/pdf/pdf-reader"

/**
 * Read-only "AI Lead" brief for an AI-created CRM prospect.
 *
 * Non-super-admin CRM viewers can't open the Sales section, but they may see
 * WHERE an AI prospect came from. This modal resolves the originating agent
 * lead via `GET /api/sales/v2/leads/by-crm/:prospectId` (gated on `crm:view`,
 * see apps/server/src/routes/sales.ts) and renders the SAME lead-brief PDF the
 * Sales page exports/shares (`buildLeadPdf`) — so the brief can never drift
 * from what a super_admin sees at /sales/leads/:id.
 *
 * The brief renders through the shared `PdfReader` (pdf.js → `<canvas>`), the
 * SAME reader the Documentation page + every other CRM PDF preview use — NEVER
 * an `<iframe>`: the Tauri desktop shells have no native PDF plugin for a
 * blob:/data: frame (WebView2 shows a broken-document placeholder, WKWebView
 * can hang). On any render failure it degrades to a Download-only card.
 */

export interface AiLeadBriefModalProps {
  open: boolean
  onClose: () => void
  /** CRM prospect id — the `crm_external_id` the agent stored on export. */
  prospectId: string
  /** Fallback title while the lead is loading. */
  prospectName?: string | undefined
}

type LoadState =
  | { phase: "loading" }
  | { phase: "ready"; lead: SalesOrgLeadDetail; pdfBytes: ArrayBuffer }
  | { phase: "error"; notFound: boolean; message: string }

export function AiLeadBriefModal({
  open,
  onClose,
  prospectId,
  prospectName,
}: AiLeadBriefModalProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ phase: "loading" })

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    setState({ phase: "loading" })
    void (async () => {
      try {
        const lead = await api<SalesOrgLeadDetail>(
          `/api/sales/v2/leads/by-crm/${encodeURIComponent(prospectId)}`,
        )
        const pdfBytes = await buildLeadPdf(lead).arrayBuffer()
        if (!cancelled) setState({ phase: "ready", lead, pdfBytes })
      } catch (err) {
        if (cancelled) return
        const notFound = err instanceof ApiError && err.status === 404
        setState({
          phase: "error",
          notFound,
          message: notFound
            ? "This prospect is no longer linked to an AI lead — the original lead may have been released or removed."
            : "The AI lead brief couldn't be loaded right now. Please try again later.",
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, prospectId])

  const lead = state.phase === "ready" ? state.lead : null
  const title = lead?.name || prospectName || "AI Lead"

  const handleDownload = async (): Promise<void> => {
    if (!lead) return
    // Native OS Save-As dialog in the desktop app; browser download on the web.
    await saveBlob(leadPdfFilename(lead), buildLeadPdf(lead))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ display: "flex", color: "primary.main" }}>
          <Sparkles size={18} />
        </Box>
        AI Lead — {title}
      </DialogTitle>
      <DialogContent dividers data-testid="ai-lead-brief-content">
        {state.phase === "loading" && (
          <Box
            data-testid="ai-lead-brief-loading"
            sx={{ display: "grid", placeItems: "center", py: 8 }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {state.phase === "error" && (
          <Typography
            data-testid="ai-lead-brief-error"
            sx={{ py: 4, textAlign: "center", color: "text.secondary", fontSize: 14 }}
          >
            {state.message}
          </Typography>
        )}

        {state.phase === "ready" && (
          <PdfReader
            bytes={state.pdfBytes}
            filename={leadPdfFilename(state.lead)}
            containerTestId="ai-lead-brief-pdf"
            fallbackTestId="ai-lead-brief-fallback"
            fallbackText="The brief couldn’t be previewed inline. Use Download to view it."
          />
        )}
      </DialogContent>
      <DialogActions>
        {lead && (
          <Button
            startIcon={<Download size={16} />}
            onClick={handleDownload}
            data-testid="ai-lead-brief-download"
          >
            Download PDF
          </Button>
        )}
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
